import React, { useEffect, useRef } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';

export default function ElementContextMenu({
  isOpen,
  x,
  y,
  elementId,
  elements,
  onClose,
  onMoveForward,
  onMoveBackward,
  onMoveToFront,
  onMoveToBack,
}) {
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Early returns must come AFTER all hooks
  if (!isOpen || !elementId) return null;

  // Find the selected element
  const selectedElement = elements.find((el) => el.id === elementId);
  if (!selectedElement) return null;

  // Check if element is at top or bottom
  const maxZ = Math.max(...elements.map((el) => (typeof el.zIndex === 'number' ? el.zIndex : 0)), 0);
  const minZ = Math.min(...elements.map((el) => (typeof el.zIndex === 'number' ? el.zIndex : 0)), 0);
  const currentZ = typeof selectedElement.zIndex === 'number' ? selectedElement.zIndex : 0;
  
  const isAtTop = currentZ === maxZ;
  const isAtBottom = currentZ === minZ;

  const menuItems = [
    {
      label: 'Bring to Top',
      icon: <ArrowUpIcon className="h-4 w-4" />,
      onClick: onMoveToFront,
      disabled: isAtTop,
      title: 'Move element to the front',
    },
    {
      label: 'Move Forward',
      icon: <ChevronUpIcon className="h-4 w-4" />,
      onClick: onMoveForward,
      disabled: isAtTop,
      title: 'Move element one layer forward',
    },
    {
      label: 'Move Backward',
      icon: <ChevronDownIcon className="h-4 w-4" />,
      onClick: onMoveBackward,
      disabled: isAtBottom,
      title: 'Move element one layer back',
    },
    {
      label: 'Move to Back',
      icon: <ArrowDownIcon className="h-4 w-4" />,
      onClick: onMoveToBack,
      disabled: isAtBottom,
      title: 'Move element to the back',
    },
  ];

  return (
    <div
      ref={menuRef}
      data-element-context-menu
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px] animate-in fade-in duration-100"
      style={{
        left: `${x}px`,
        top: `${y}px`,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {menuItems.map((item, index) => (
        <button
          key={index}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          disabled={item.disabled}
          title={item.title}
          className={`w-full px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
            item.disabled
              ? 'text-gray-400 cursor-not-allowed bg-gray-50'
              : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer'
          }`}
        >
          <span className="flex-shrink-0">{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}
